import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Tag, Spin, Empty, Statistic, Tabs, Progress, Tooltip, Space, Badge } from 'antd';
import { TrophyOutlined, StarOutlined, FireOutlined, CrownOutlined, CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import achievementService from '../services/achievementService';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/Common/PageHeader';
import './Achievements.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Achievements = () => {
    const [loading, setLoading] = useState(true);
    const [achievements, setAchievements] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        loadData();
    }, [activeTab]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [achievementsData, statsData] = await Promise.all([
                achievementService.getAllAchievements(),
                achievementService.getStatistics()
            ]);

            setAchievements(achievementsData.achievements || []);
            setStatistics(statsData);
        } catch (error) {
            console.error('Error loading achievements:', error);
        } finally {
            setLoading(false);
        }
    };

    const getAchievementsByCategory = (category) => {
        return achievements.filter(a => a.category === category);
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'bidding': return <FireOutlined />;
            case 'winning': return <TrophyOutlined />;
            case 'social': return <StarOutlined />;
            default: return <CrownOutlined />;
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'bidding': return '#1890ff';
            case 'winning': return '#52c41a';
            case 'social': return '#722ed1';
            default: return '#faad14';
        }
    };

    const renderAchievementCard = (achievement) => {
        const isUnlocked = achievement.unlocked;
        const opacity = isUnlocked ? 1 : 0.5;

        return (
            <Col xs={24} sm={12} md={8} lg={6} key={achievement.id}>
                <Card
                    className={`achievement-card ${isUnlocked ? 'unlocked' : 'locked'}`}
                    style={{
                        marginBottom: 16,
                        opacity,
                        border: isUnlocked ? `2px solid ${achievement.badge_color}` : '2px solid #d9d9d9',
                        background: isUnlocked 
                            ? `linear-gradient(135deg, ${achievement.badge_color}15 0%, ${achievement.badge_color}05 100%)`
                            : '#fafafa'
                    }}
                    hoverable={isUnlocked}
                >
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '48px', marginBottom: 12 }}>
                            {achievement.icon}
                        </div>
                        {!isUnlocked && (
                            <div style={{ position: 'absolute', top: 8, right: 8 }}>
                                <LockOutlined style={{ fontSize: '20px', color: '#d9d9d9' }} />
                            </div>
                        )}
                        <Title level={5} style={{ marginBottom: 8 }}>
                            {achievement.name}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 12 }}>
                            {achievement.description}
                        </Text>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                            <Tag color={achievement.badge_color} style={{ margin: 0 }}>
                                {getCategoryIcon(achievement.category)} {achievement.category}
                            </Tag>
                            <Badge 
                                count={achievement.points} 
                                style={{ backgroundColor: achievement.badge_color }}
                                title={`${achievement.points} points`}
                            />
                        </div>
                        {isUnlocked && achievement.unlocked_at && (
                            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 8 }}>
                                Unlocked: {new Date(achievement.unlocked_at).toLocaleDateString()}
                            </Text>
                        )}
                        {!isUnlocked && achievement.progress > 0 && (
                            <div style={{ marginTop: 12 }}>
                                <Progress 
                                    percent={achievement.progress} 
                                    size="small" 
                                    strokeColor={achievement.badge_color}
                                />
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    {achievement.progress}% complete
                                </Text>
                            </div>
                        )}
                    </div>
                </Card>
            </Col>
        );
    };

    const unlockedCount = achievements.filter(a => a.unlocked).length;
    const totalCount = achievements.length;
    const unlockedPercentage = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

    return (
        <DashboardLayout>
            <PageHeader
                title="Achievements & Badges"
                description="Track your bidding progress and unlock achievements"
            />

            <Spin spinning={loading}>
                {/* Statistics Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Points"
                                value={statistics?.total_points || 0}
                                prefix={<StarOutlined style={{ color: '#faad14' }} />}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Achievements Unlocked"
                                value={unlockedCount}
                                suffix={`/ ${totalCount}`}
                                prefix={<TrophyOutlined style={{ color: '#52c41a' }} />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Bids"
                                value={statistics?.total_bids || 0}
                                prefix={<FireOutlined style={{ color: '#1890ff' }} />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Wins"
                                value={statistics?.winning_bids || 0}
                                prefix={<CrownOutlined style={{ color: '#722ed1' }} />}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Progress Bar */}
                <Card style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 8 }}>
                        <Text strong>Overall Progress</Text>
                        <Text style={{ float: 'right', color: '#52c41a' }}>
                            {unlockedPercentage}% Complete
                        </Text>
                    </div>
                    <Progress 
                        percent={unlockedPercentage} 
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }}
                        strokeWidth={12}
                    />
                </Card>

                {/* Achievements Tabs */}
                <Card>
                    <Tabs 
                        activeKey={activeTab} 
                        onChange={setActiveTab}
                        items={[
                            {
                                key: 'all',
                                label: (
                                    <span>
                                        All Achievements
                                        <Badge count={totalCount} style={{ marginLeft: 8 }} />
                                    </span>
                                ),
                                children: (
                                    <Row gutter={[16, 16]}>
                                        {achievements.length > 0 ? (
                                            achievements.map(renderAchievementCard)
                                        ) : (
                                            <Col span={24}>
                                                <Empty description="No achievements available" />
                                            </Col>
                                        )}
                                    </Row>
                                )
                            },
                            {
                                key: 'bidding',
                                label: (
                                    <span>
                                        Bidding
                                        <Badge 
                                            count={getAchievementsByCategory('bidding').filter(a => a.unlocked).length} 
                                            style={{ marginLeft: 8 }} 
                                        />
                                    </span>
                                ),
                                children: (
                                    <Row gutter={[16, 16]}>
                                        {getAchievementsByCategory('bidding').length > 0 ? (
                                            getAchievementsByCategory('bidding').map(renderAchievementCard)
                                        ) : (
                                            <Col span={24}>
                                                <Empty description="No bidding achievements" />
                                            </Col>
                                        )}
                                    </Row>
                                )
                            },
                            {
                                key: 'winning',
                                label: (
                                    <span>
                                        Winning
                                        <Badge 
                                            count={getAchievementsByCategory('winning').filter(a => a.unlocked).length} 
                                            style={{ marginLeft: 8 }} 
                                        />
                                    </span>
                                ),
                                children: (
                                    <Row gutter={[16, 16]}>
                                        {getAchievementsByCategory('winning').length > 0 ? (
                                            getAchievementsByCategory('winning').map(renderAchievementCard)
                                        ) : (
                                            <Col span={24}>
                                                <Empty description="No winning achievements" />
                                            </Col>
                                        )}
                                    </Row>
                                )
                            }
                        ]}
                    />
                </Card>
            </Spin>
        </DashboardLayout>
    );
};

export default Achievements;

